import {
  LAGOS_LGAS,
  LAGOS_NEIGHBORHOODS,
  LAGOS_STATE,
  OYO_LGAS,
  OYO_NEIGHBORHOODS,
  OYO_STATE,
  parseLocationString,
} from "@/lib/locationUtils";

type NullableId = string | null;

export type ResolvedLocationIds = {
  locationStateId: NullableId;
  locationLgaId: NullableId;
  locationNeighborhoodId: NullableId;
};

function norm(value: string | null | undefined): string {
  return (value ?? "").trim().toLowerCase();
}

const ALL_STATES = [LAGOS_STATE, OYO_STATE];
const ALL_LGAS = [...LAGOS_LGAS, ...OYO_LGAS];
const ALL_NEIGHBORHOODS = [...LAGOS_NEIGHBORHOODS, ...OYO_NEIGHBORHOODS];
const CANONICAL_STATE_IDS = new Set(ALL_STATES.map((state) => state.id));

function isCanonicalStateId(stateId: string): boolean {
  return CANONICAL_STATE_IDS.has(stateId);
}

function canonicalStateIdForName(
  stateName: string | null | undefined,
): string | null {
  const normalizedName = norm(stateName);
  if (!normalizedName) return null;
  return (
    ALL_STATES.find((state) => norm(state.name) === normalizedName)?.id ?? null
  );
}

function inferCanonicalStateIdFromLgaId(lgaId: string | null): string | null {
  if (!lgaId) return null;
  const lga = ALL_LGAS.find((entry) => entry.id === lgaId);
  return lga?.parent && isCanonicalStateId(lga.parent) ? lga.parent : null;
}

function inferCanonicalStateIdFromNeighborhoodId(
  neighborhoodId: string | null,
): string | null {
  if (!neighborhoodId) return null;
  const neighborhood = ALL_NEIGHBORHOODS.find(
    (entry) => entry.id === neighborhoodId,
  );
  if (!neighborhood?.parent) return null;
  return inferCanonicalStateIdFromLgaId(neighborhood.parent);
}

function resolveCanonicalStateId(
  state: { id: string; name: string } | null,
): string | null {
  if (!state) return null;
  const byName = canonicalStateIdForName(state.name);
  if (byName) return byName;
  if (isCanonicalStateId(state.id)) return state.id;
  return state.id;
}

/** Map DB UUID state ids (and other non-slug values) to canonical slugs when possible. */
export function normalizeResolvedLocationIds(
  ids: ResolvedLocationIds,
  fallbackLocation?: string | null,
): ResolvedLocationIds {
  const currentStateId = ids.locationStateId;
  if (currentStateId && isCanonicalStateId(currentStateId)) {
    return ids;
  }

  const fromLga = inferCanonicalStateIdFromLgaId(ids.locationLgaId);
  const fromNeighborhood = inferCanonicalStateIdFromNeighborhoodId(
    ids.locationNeighborhoodId,
  );
  const fromLocationString = fallbackLocation?.trim()
    ? canonicalStateIdForName(
        parseLocationString(fallbackLocation.trim()).state,
      )
    : null;

  const canonicalStateId =
    fromLga ??
    fromNeighborhood ??
    (fromLocationString && isCanonicalStateId(fromLocationString)
      ? fromLocationString
      : null);

  if (!canonicalStateId) {
    return ids;
  }

  return {
    ...ids,
    locationStateId: canonicalStateId,
  };
}

/** Structured ids from the hierarchical picker (state may come from DB as a UUID). */
export function locationIdsFromHierarchy(
  state: { id: string; name: string } | null,
  lga: { id: string } | null = null,
  neighborhood: { id: string } | null = null,
): ResolvedLocationIds {
  return normalizeResolvedLocationIds({
    locationStateId: resolveCanonicalStateId(state),
    locationLgaId: lga?.id ?? null,
    locationNeighborhoodId: neighborhood?.id ?? null,
  });
}

/** Callback shape for `HierarchicalLocationSelect` (optional structured ids). */
export type HierarchicalLocationOnChange = (
  value: string,
  coordinates?: import("@/lib/types").Coordinates,
  placeId?: string,
  locationIds?: ResolvedLocationIds,
) => void;

export function resolveLocationIdsFromLocationString(
  location: string | null | undefined,
): ResolvedLocationIds {
  if (!location?.trim()) {
    return emptyResolvedLocationIds();
  }

  const parsed = parseLocationString(location.trim());
  const stateName = norm(parsed.state);
  const lgaName = norm(parsed.lga);
  const neighborhoodName = norm(parsed.neighborhood);

  if (!stateName && !lgaName && !neighborhoodName) {
    return emptyResolvedLocationIds();
  }

  const state = ALL_STATES.find((s) => norm(s.name) === stateName) ?? null;

  const lga =
    ALL_LGAS.find((l) => {
      if (norm(l.name) !== lgaName) return false;
      if (!state) return true;
      return l.parent === state.id;
    }) ?? null;

  const neighborhood =
    ALL_NEIGHBORHOODS.find((n) => {
      if (norm(n.name) !== neighborhoodName) return false;
      if (!lga) return true;
      return n.parent === lga.id;
    }) ?? null;

  return normalizeResolvedLocationIds(
    {
      locationStateId: state?.id ?? null,
      locationLgaId: lga?.id ?? null,
      locationNeighborhoodId: neighborhood?.id ?? null,
    },
    location.trim(),
  );
}

function emptyResolvedLocationIds(): ResolvedLocationIds {
  return {
    locationStateId: null,
    locationLgaId: null,
    locationNeighborhoodId: null,
  };
}

function hasAnyResolvedLocationId(ids: ResolvedLocationIds): boolean {
  return Boolean(
    ids.locationStateId || ids.locationLgaId || ids.locationNeighborhoodId,
  );
}

/** Prefer structured ids from the picker; fall back to parsing the location string. */
export function mergeResolvedLocationIds(
  primary: ResolvedLocationIds | undefined,
  fallbackLocation: string | null | undefined,
): ResolvedLocationIds {
  const merged =
    primary && hasAnyResolvedLocationId(primary)
      ? primary
      : resolveLocationIdsFromLocationString(fallbackLocation);

  return normalizeResolvedLocationIds(merged, fallbackLocation);
}
